import { useState } from 'react';
import { Link } from 'react-router-dom';
import { api, ifMatchFor } from '../api/client';
import { qk, useAbsences, useDevices, useOverrides, useRooms, useWakeHistory, useWakes } from '../api/queries';
import type { LongAbsence, Override, Wake } from '../api/types';
import { useAction } from '../components/feedback';
import { Async, Badge, Card, Field, ModeChip, PageHeader } from '../components/ui';
import { byId, deviceName, roomName } from '../lib/domain';
import { dayTime, isoToLocalInput, localInputToIso, parisMinutes, parseHHMM, time } from '../lib/time';

export function EventsPage() {
  return (
    <>
      <PageHeader title="Réveil, forçages, absences" subtitle="Priorités : suspension, mesure, forçage, absence longue, routine." />
      <div className="col-grid">
        <WakeSection />
        <OverridesSection />
        <AbsencesSection />
      </div>
    </>
  );
}

const NOTIF_TONE = { pending: 'info', sent: 'ok', failed: 'bad', expired: 'neutral' } as const;
const NOTIF_LABEL = { pending: 'scène prévue', sent: 'scène envoyée', failed: 'scène en échec', expired: 'scène expirée' } as const;

function WakeCard({ w }: { w: Wake }) {
  const { run, busy } = useAction();
  const rooms = useRooms();
  const roomMap = byId(rooms.data);
  const [edit, setEdit] = useState<string | null>(null);
  const path = `/events/wake/${w.id}`;
  const byRoom = new Map<string, { preheat: string; floor: string; count: number }>();
  for (const p of w.wake_phases ?? []) {
    const cur = byRoom.get(p.room_id);
    byRoom.set(p.room_id, {
      preheat: !cur || p.preheat_start_at < cur.preheat ? p.preheat_start_at : cur.preheat,
      floor: !cur || p.floor_end_at > cur.floor ? p.floor_end_at : cur.floor,
      count: (cur?.count ?? 0) + 1,
    });
  }
  const inv = [['wakes'], qk.state, qk.measurementStatus];

  return (
    <div className="panel stack-s">
      <div className="row between gap-s wrap">
        <div>
          <div className="mono big">{time(w.scheduled_at)}</div>
          <div className="small muted">{dayTime(w.scheduled_at)} · déclaré {w.declared_time}</div>
        </div>
        <Badge tone={w.status === 'scheduled' ? 'ok' : 'neutral'}>{w.status === 'scheduled' ? 'Programmé' : w.status === 'triggered' ? 'Déclenché' : 'Annulé'}</Badge>
      </div>
      {[...byRoom.entries()].map(([room, v]) => (
        <div key={room} className="row between small">
          <span>{roomName(roomMap, room)} <span className="muted">({v.count} radiateur(s))</span></span>
          <span className="mono">{time(v.preheat)} → {time(v.floor)}</span>
        </div>
      ))}
      {(w.warnings ?? []).map((x, i) => <p key={i} className="alert alert-warn small">{Object.values(x).join(' · ')}</p>)}
      {(w.notifications ?? []).map((n, i) => (
        <div key={i} className="small row gap-s wrap">
          <Badge tone={NOTIF_TONE[n.status]}>{NOTIF_LABEL[n.status]}</Badge>
          {n.sent_at && <span className="muted">{time(n.sent_at)}{n.http_status ? ` · HTTP ${n.http_status}` : ''}</span>}
          {n.attempts > 1 && <span className="muted">{n.attempts} tentatives</span>}
          {n.last_error && <span className="text-bad">{n.last_error}</span>}
        </div>
      ))}
      {w.status === 'scheduled' && (edit === null ? (
        <div className="row gap-s">
          <button type="button" className="btn btn-s" onClick={() => setEdit(isoToLocalInput(w.scheduled_at))}>Modifier</button>
          <button type="button" className="btn btn-s btn-danger" disabled={busy}
            onClick={() => run(() => api.send('DELETE', path, { ifMatch: ifMatchFor(path, w.version) }), { success: 'Réveil annulé (historique conservé)', invalidate: inv })}>
            Annuler
          </button>
        </div>
      ) : (
        <form className="row gap-s wrap align-end" onSubmit={(e) => {
          e.preventDefault();
          const iso = localInputToIso(edit);
          if (!iso) return;
          run((confirmation) => api.send<Wake>('PATCH', path, { body: { scheduled_at: iso }, ifMatch: ifMatchFor(path, w.version), confirmation }).then((r) => r.data), {
            success: (r) => `Réveil déplacé à ${time(r.scheduled_at)}`, invalidate: inv,
          }).then(() => setEdit(null));
        }}>
          <Field label="Nouvel horaire" htmlFor={`we-${w.id}`}><input id={`we-${w.id}`} type="datetime-local" value={edit} onChange={(e) => setEdit(e.target.value)} /></Field>
          <button className="btn btn-s btn-primary" disabled={busy}>Valider</button>
          <button type="button" className="btn btn-s" onClick={() => setEdit(null)}>Annuler</button>
        </form>
      ))}
    </div>
  );
}

function WakeSection() {
  const { run, busy } = useAction();
  const scheduled = useWakes('scheduled');
  const history = useWakeHistory();
  const [t, setT] = useState('07:15');
  const m = parseHHMM(t);
  const tomorrow = m !== null && m <= parisMinutes();

  return (
    <Card title="Réveil">
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        run((confirmation) => api.send<Wake>('POST', '/events/wake', { body: { time: t }, confirmation }), {
          success: (r) => `${r.status === 200 ? 'Réveil mis à jour' : 'Réveil programmé'} : ${dayTime(r.data.scheduled_at)}${r.data.warnings?.length ? ' · aucune phase Réveil applicable' : ''}`,
          invalidate: [['wakes'], qk.state, qk.measurementStatus],
        });
      }}>
        <Field label="Heure de réveil" htmlFor="w-time" hint={m === null ? undefined : `Vise ${tomorrow ? 'demain' : 'aujourd’hui'} · heure de Paris`}>
          <input id="w-time" type="time" className="input-xl mono" value={t} onChange={(e) => setT(e.target.value)} required />
        </Field>
        <button className="btn btn-primary btn-block" disabled={busy || m === null}>Déclarer le réveil</button>
        <p className="small muted">Une seule occurrence, jamais reconduite. Une nouvelle déclaration pour la même date remplace la précédente. Refusé pendant une absence longue.</p>
      </form>
      <div className="divider" />
      <div className="stack-s">
        <div className="small strong">Programmés</div>
        <Async q={{ data: scheduled.data?.data, isLoading: scheduled.isLoading, error: scheduled.error }} empty={<p className="small muted">Aucun réveil programmé.</p>}>
          {(list) => list.map((w) => <WakeCard key={w.id} w={w} />)}
        </Async>
      </div>
      <div className="divider" />
      <details>
        <summary className="small strong">Journal des déclarations</summary>
        <Async q={{ data: history.data?.data, isLoading: history.isLoading, error: history.error }} empty={<p className="small muted">Vide.</p>}>
          {(list) => (
            <ul className="timeline-list">
              {list.map((d) => (
                <li key={d.id}>
                  <span className="mono muted">{dayTime(d.received_at)}</span>
                  <span className="small">{d.declared_time} pour le {d.target_date} · {d.status === 'created' ? 'créé' : d.status === 'replaced' ? 'remplacé' : 'modifié'}</span>
                </li>
              ))}
            </ul>
          )}
        </Async>
      </details>
    </Card>
  );
}

function OverridesSection() {
  const { run, busy } = useAction();
  const active = useOverrides(true);
  const recent = useOverrides(false);
  const rooms = useRooms();
  const devices = useDevices();
  const roomMap = byId(rooms.data);
  const devMap = byId(devices.data);
  const scopeLabel = (o: Override) =>
    o.scope === 'home' ? 'Domicile' : o.scope === 'room' ? `Pièce · ${roomName(roomMap, o.target_id)}` : `Radiateur · ${deviceName(devMap.get(o.target_id ?? ''))}`;
  const progress = (o: Override) => {
    const a = new Date(o.starts_at).getTime(), b = new Date(o.ends_at).getTime();
    return Math.min(100, Math.max(0, ((Date.now() - a) / (b - a)) * 100));
  };

  return (
    <Card title="Forçages" actions={<Link to="/" className="small">Nouveau forçage</Link>}>
      <div className="stack">
        <div className="small strong">Actifs</div>
        <Async q={{ data: active.data?.data, isLoading: active.isLoading, error: active.error }} empty={<p className="small muted">Aucun forçage actif.</p>}>
          {(list) => list.map((o) => (
            <div key={o.id} className="panel stack-s">
              <div className="row between wrap gap-s"><ModeChip mode={o.mode} /><span className="small muted">{scopeLabel(o)}</span></div>
              <div className="progress" role="progressbar" aria-valuenow={Math.round(progress(o))} aria-valuemin={0} aria-valuemax={100} aria-label="Temps écoulé">
                <div className={`progress-bar mode-${o.mode}`} style={{ width: `${progress(o)}%` }} />
              </div>
              <div className="row between mono xsmall muted"><span>{time(o.starts_at)}</span><span>fin {dayTime(o.ends_at)}</span></div>
              <button type="button" className="btn btn-s" disabled={busy}
                onClick={() => run(() => api.send('DELETE', `/overrides/${o.id}`), { success: 'Forçage arrêté', invalidate: [['overrides'], qk.state] })}>
                Arrêter maintenant
              </button>
            </div>
          ))}
        </Async>
        <p className="xsmall muted">Un nouveau forçage sur la même portée remplace le précédent. Entre portées, le plus spécifique gagne.</p>
        <div className="divider" />
        <div className="small strong">Historique</div>
        <Async q={{ data: recent.data?.data, isLoading: recent.isLoading, error: recent.error }} empty={<p className="small muted">Aucun.</p>}>
          {(list) => (
            <ul className="timeline-list">
              {list.map((o) => (
                <li key={o.id}>
                  <span className="mono muted">{dayTime(o.starts_at)}</span>
                  <span className="small"><ModeChip mode={o.mode} /> {scopeLabel(o)} · jusqu’à {time(o.ends_at)}{o.cancelled_long_absence_ids?.length ? ' · a annulé une absence' : ''}</span>
                </li>
              ))}
            </ul>
          )}
        </Async>
      </div>
    </Card>
  );
}

function AbsenceRow({ a }: { a: LongAbsence }) {
  const { run, busy } = useAction();
  const [end, setEnd] = useState<string | null>(null);
  const path = `/long-absences/${a.id}`;
  const inv = [['absences'], qk.state, ['wakes']];
  return (
    <div className="panel stack-s">
      <div className="row between wrap gap-s">
        <span className="mono">{dayTime(a.start)} → {dayTime(a.end)}</span>
        <Badge tone={a.status === 'active' ? 'warn' : 'info'}>{a.status === 'active' ? 'En cours' : 'Programmée'}</Badge>
      </div>
      {end === null ? (
        <div className="row gap-s">
          <button type="button" className="btn btn-s" onClick={() => setEnd(isoToLocalInput(a.end))}>{a.status === 'active' ? 'Prolonger / raccourcir' : 'Modifier la fin'}</button>
          <button type="button" className="btn btn-s btn-danger" disabled={busy}
            onClick={() => run(() => api.send('DELETE', path, { ifMatch: ifMatchFor(path, a.version) }), { success: a.status === 'active' ? 'Absence arrêtée : programmation normale rétablie' : 'Absence supprimée', invalidate: inv })}>
            {a.status === 'active' ? 'Arrêter maintenant' : 'Supprimer'}
          </button>
        </div>
      ) : (
        <form className="row gap-s wrap align-end" onSubmit={(e) => {
          e.preventDefault();
          const iso = localInputToIso(end);
          if (!iso) return;
          run(() => api.send('PATCH', path, { body: { end: iso }, ifMatch: ifMatchFor(path, a.version) }), { success: 'Fin modifiée', invalidate: inv }).then(() => setEnd(null));
        }}>
          <Field label="Nouvelle fin" htmlFor={`ae-${a.id}`}><input id={`ae-${a.id}`} type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} /></Field>
          <button className="btn btn-s btn-primary" disabled={busy}>Valider</button>
          <button type="button" className="btn btn-s" onClick={() => setEnd(null)}>Annuler</button>
        </form>
      )}
    </div>
  );
}

function AbsencesSection() {
  const { run, busy } = useAction();
  const active = useAbsences('active');
  const scheduled = useAbsences('scheduled');
  const ended = useAbsences('ended');
  const cancelled = useAbsences('cancelled');
  const [start, setStart] = useState('');
  const [end, setEnd] = useState('');
  const current = [...(active.data ?? []), ...(scheduled.data ?? [])];
  const past = [...(ended.data ?? []), ...(cancelled.data ?? [])].sort((a, b) => b.start.localeCompare(a.start)).slice(0, 5);

  return (
    <Card title="Absences longues">
      <form className="stack" onSubmit={(e) => {
        e.preventDefault();
        const endIso = localInputToIso(end);
        if (!endIso) return;
        const startIso = start ? localInputToIso(start) : null;
        run(() => api.send<LongAbsence>('POST', '/events/long-absence', { body: { end: endIso, ...(startIso ? { start: startIso } : {}) } }), {
          success: (r) => (r.data.merged ? `Fusionnée : ${dayTime(r.data.start)} → ${dayTime(r.data.end)}` : 'Absence programmée'),
          invalidate: [['absences'], qk.state, ['wakes']],
        }).then((r) => { if (r) { setStart(''); setEnd(''); } });
      }}>
        <p className="small">Domicile entier en Hors-gel. Au démarrage, les réveils programmés sont annulés.</p>
        <div className="grid-2">
          <Field label="Début" htmlFor="a-start" hint="Vide = maintenant"><input id="a-start" type="datetime-local" value={start} onChange={(e) => setStart(e.target.value)} /></Field>
          <Field label="Fin" htmlFor="a-end" hint="Obligatoire"><input id="a-end" type="datetime-local" value={end} onChange={(e) => setEnd(e.target.value)} required /></Field>
        </div>
        <button className="btn btn-primary btn-block" disabled={busy || !end}>Programmer l’absence</button>
      </form>
      <div className="divider" />
      <div className="stack-s">
        <Async q={{ data: current, isLoading: active.isLoading || scheduled.isLoading, error: active.error ?? scheduled.error }} empty={<p className="small muted">Aucune absence en cours ou programmée.</p>}>
          {(list) => list.map((a) => <AbsenceRow key={a.id} a={a} />)}
        </Async>
        {past.length > 0 && (
          <details>
            <summary className="small strong">Passées</summary>
            <ul className="timeline-list">
              {past.map((a) => <li key={a.id}><span className="mono muted">{dayTime(a.start)}</span><span className="small">→ {dayTime(a.end)} · {a.cancelled ? 'annulée' : 'terminée'}{a.merged_into_id ? ' · fusionnée' : ''}</span></li>)}
            </ul>
          </details>
        )}
        <p className="xsmall muted">En cours, seule la fin est modifiable, jamais vers le passé : pour terminer tout de suite, arrêtez l’absence.</p>
      </div>
    </Card>
  );
}
